import { useQuery } from "@tanstack/react-query";
import { useState, type ReactElement } from "react";
import type { ListResult, ProductListItem } from "@cx/contracts";
import { formatBRL } from "@cx/domain";
import { useSession } from "../../session";

/**
 * Busca de produto vendável (simples, variação ou kit; o pai de variação fica fora), pela mesma busca FTS da lista.
 * Mostra o preço de venda do cadastro; nunca o custo.
 */
export function ProductSearch({
  id,
  label,
  action,
  exclude = [],
  onPick,
}: {
  id: string;
  label: string;
  /** Texto do botão de cada resultado; o aria-label leva o nome do produto. */
  action: string;
  exclude?: string[];
  onPick(p: ProductListItem): void;
}): ReactElement {
  const { companyId, api } = useSession();
  const [q, setQ] = useState("");
  const term = q.trim();
  const found = useQuery({
    queryKey: ["products", companyId, { q: term, picker: "sellable" }],
    enabled: term.length >= 2,
    queryFn: () =>
      api<ListResult<ProductListItem>>(
        `/products?${new URLSearchParams({ q: term, includeVariations: "yes", limit: "8" })}`,
      ),
  });
  const rows = (found.data?.data ?? []).filter((p) => p.format !== "V" && !exclude.includes(p.id));
  return (
    <div className="mb-3">
      <label className="form-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        type="search"
        className="form-control"
        placeholder="Digite nome, SKU ou GTIN do produto"
        value={q}
        onChange={(e) => setQ(e.target.value)}
      />
      {term.length >= 2 && (
        <ul className="list-group mt-1" aria-label="Produtos encontrados">
          {found.isLoading && <li className="list-group-item text-muted">Buscando…</li>}
          {found.isSuccess && rows.length === 0 && (
            <li className="list-group-item text-muted">Nenhum produto encontrado.</li>
          )}
          {rows.map((p) => (
            <li key={p.id} className="list-group-item d-flex align-items-center gap-2">
              <span className="flex-grow-1">
                {p.name}
                {p.sku && <span className="text-muted small ms-2">{p.sku}</span>}
                <span className="text-muted small ms-2">{formatBRL(p.priceCents)}</span>
              </span>
              <button
                type="button"
                className="btn btn-sm btn-outline-primary"
                aria-label={`${action} ${p.name}`}
                onClick={() => {
                  onPick(p);
                  setQ("");
                }}
              >
                {action}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
